/*
 * Copyright 2026 Sony Corporation
 */

import { SSAO2RenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/ssao2RenderingPipeline';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import type { Scene } from '@babylonjs/core/scene';

// SSAO2 reads normals and depth from the geometry buffer, which is a scene
// component the ES6 build only installs when it is asked for by name.
import '@babylonjs/core/Rendering/geometryBufferRendererSceneComponent';

export interface AmbientOcclusionOptions {
  scene: Scene;
  /** The viewport camera. The device preview keeps its own chain and is left alone. */
  camera: Camera;
}

/**
 * Contact shading, which is the cheapest realism left once IBL is doing the lighting.
 *
 * An HDRI lights every surface from every direction, and that is exactly why a
 * scene lit by one looks pasted together: nothing occludes the ambient, so a
 * wheel meets the ground with no darkening at the contact. SSAO puts that back.
 *
 * Editor-only, deliberately. It is a screen-space effect on the viewport
 * camera, so `SceneSerializer` never sees it (devplan §5.5) and a published
 * `.babylon` is unchanged — what the Camera page loads is still geometry and
 * materials, not a look.
 */
export interface AmbientOcclusion {
  readonly pipeline: SSAO2RenderingPipeline;
  dispose: () => void;
}

/** Half res. The occlusion term is low frequency, and full res buys nothing a blur does not undo. */
const SSAO_RATIO = 0.5;

/** How far a sample may reach for an occluder, in world units, before it is a different surface. */
const RADIUS = 0.6;

export const createAmbientOcclusion = ({ scene, camera }: AmbientOcclusionOptions): AmbientOcclusion => {
  const pipeline = new SSAO2RenderingPipeline('EditorSSAO', scene, { ssaoRatio: SSAO_RATIO, blurRatio: 1 }, [camera]);

  pipeline.radius = RADIUS;
  // Strong enough to read as contact, short of the grubby outline that gives
  // screen-space occlusion away.
  pipeline.totalStrength = 1.1;
  pipeline.expensiveBlur = true;
  pipeline.samples = 16;
  // Flat surfaces occlude themselves at low sample counts; this is the usual
  // cure for the resulting speckle.
  pipeline.epsilon = 0.02;

  return {
    pipeline,
    dispose: () => {
      scene.postProcessRenderPipelineManager.detachCamerasFromRenderPipeline(pipeline.name, camera);
      pipeline.dispose();
    },
  };
};
